import React, { useState, useMemo } from 'react';
import { Plus, Container, Edit, Trash, Search, ArrowRight } from 'lucide-react';
import useAppStore from '../store';
import DumpsterMoveToDepotForm from '../components/DumpsterMoveToDepotForm';

const DumpstersPage: React.FC = () => {
  const { dumpsters, addDumpster, updateDumpster, deleteDumpster, toggleDumpsterActiveStatus, clients, depots, currentUser, loadInitialData } = useAppStore();
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showMoveToDepotModal, setShowMoveToDepotModal] = useState(false);
  const [selectedDumpster, setSelectedDumpster] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // New dumpster form state
  const [newDumpster, setNewDumpster] = useState({
    name: '',
    number: '',
    comment: '',
  });
  
  // Edit dumpster form state
  const [editDumpster, setEditDumpster] = useState({
    name: '',
    number: '',
    comment: '',
  });

  // Sort and filter dumpsters
  const sortedAndFilteredDumpsters = useMemo(() => {
    return dumpsters
      .filter(dumpster => 
        dumpster.number.toLowerCase().includes(searchTerm.toLowerCase()) ||
        dumpster.name.toLowerCase().includes(searchTerm.toLowerCase())
      )
      .sort((a, b) => {
        // Sort by number alphabetically
        return a.number.localeCompare(b.number, undefined, { numeric: true });
      });
  }, [dumpsters, searchTerm]);
  
  // Handle form change for new dumpster
  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setNewDumpster(prev => ({ ...prev, [name]: value }));
  };
  
  // Handle form change for edit dumpster
  const handleEditFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setEditDumpster(prev => ({ ...prev, [name]: value }));
  };
  
  // Handle form submit for new dumpster
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);
    
    try {
      if (!newDumpster.name || !newDumpster.number) {
        setError('Veuillez remplir tous les champs obligatoires');
        setIsSubmitting(false);
        return;
      }
      
      await addDumpster(newDumpster);
      setNewDumpster({
        name: '',
        number: '',
        comment: '',
      });
      setShowAddModal(false);
    } catch (error) {
      console.error('Error adding dumpster:', error);
      setError('Une erreur est survenue lors de la création de la benne. Veuillez réessayer.');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  // Handle form submit for edit dumpster
  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);
    
    if (!selectedDumpster) {
      setError('Aucune benne sélectionnée');
      setIsSubmitting(false);
      return;
    }
    
    try {
      await updateDumpster(selectedDumpster, editDumpster);
      setShowEditModal(false);
      setSelectedDumpster(null);
    } catch (error) {
      console.error('Error updating dumpster:', error);
      setError('Une erreur est survenue lors de la modification de la benne');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  // Handle delete dumpster
  const handleDelete = async () => {
    if (!selectedDumpster) return;
    setIsSubmitting(true);
    
    try {
      await deleteDumpster(selectedDumpster);
      setShowDeleteModal(false);
      setSelectedDumpster(null);
    } catch (error) {
      console.error('Error deleting dumpster:', error);
      setError('Une erreur est survenue lors de la suppression de la benne');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleToggleActiveStatus = async (dumpsterId: string, currentStatus: boolean) => {
    try {
      await toggleDumpsterActiveStatus(dumpsterId, !currentStatus);
      await loadInitialData();
    } catch (error) {
      console.error('Error toggling dumpster status:', error);
      alert('Une erreur est survenue lors de la modification du statut de la benne');
    }
  };

  // Function to determine if the "Move to Depot" button should be shown
  const shouldShowMoveToDepotButton = (dumpster: any) => {
    // Show button if dumpster is already in a depot
    if (dumpster.depotId) return true;
    
    // Show button if dumpster has no position (newly created)
    if (!dumpster.position && !dumpster.clientId) return true;
    
    // Show button if dumpster is at a client location (allow moving to depot)
    if (dumpster.clientId) return true;
    
    // Default to showing the button
    return true;
  };

  // Fonction pour obtenir le statut détaillé d'une benne
  const getDumpsterStatus = (dumpster: any) => {
    // Si la benne est chez un client
    if (dumpster.clientId) {
      const client = clients.find(c => c.id === dumpster.clientId);
      return {
        type: 'client',
        label: 'Chez client',
        details: client ? client.companyName : 'Client inconnu',
        className: 'bg-green-100 text-green-800'
      };
    }
    
    // Si la benne est dans un dépôt
    if (dumpster.depotId) {
      const depot = depots.find(d => d.id === dumpster.depotId);
      return {
        type: 'depot',
        label: 'Au dépôt',
        details: depot ? depot.name : 'Dépôt inconnu',
        className: 'bg-blue-100 text-blue-800'
      };
    }

    // Si la benne a une position mais n'est ni chez un client ni dans un dépôt
    if (dumpster.position) {
      return {
        type: 'transit',
        label: 'En transit',
        details: 'Position GPS disponible',
        className: 'bg-yellow-100 text-yellow-800'
      };
    }

    // Si la benne n'a pas de position
    return {
      type: 'unknown',
      label: 'Position inconnue',
      details: 'Aucune information de localisation',
      className: 'bg-gray-100 text-gray-800'
    };
  };
  
  return (
    <div>
      <div className="bg-white rounded-lg shadow mb-4 p-4 flex justify-between items-center">
        <h2 className="text-xl font-bold">Liste des bennes ({sortedAndFilteredDumpsters.length})</h2>
        
        <button
          onClick={() => setShowAddModal(true)}
          className="bg-blue-800 text-white px-4 py-2 rounded-lg flex items-center"
        >
          <Plus className="h-4 w-4 mr-2" />
          Ajouter une benne
        </button>
      </div>

      <div className="bg-white rounded-lg shadow mb-4 p-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
          <input
            type="text"
            placeholder="Rechercher par numéro..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
      </div>
      
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Benne
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Numéro
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Commentaire
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Statut actif
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Statut
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {sortedAndFilteredDumpsters.map(dumpster => {
              const status = getDumpsterStatus(dumpster);
              const showMoveToDepotButton = shouldShowMoveToDepotButton(dumpster);

              return (
                <tr key={dumpster.id}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="bg-yellow-100 text-yellow-800 rounded-full h-8 w-8 flex items-center justify-center mr-3">
                        <Container className="h-4 w-4" />
                      </div>
                      <div className="font-medium">{dumpster.name}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {dumpster.number}
                  </td>
                  <td className="px-6 py-4">
                    {dumpster.comment || '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        dumpster.isActive !== false ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {dumpster.isActive !== false ? 'Actif' : 'Inactif'}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${status.className}`}>
                        {status.label}
                      </span>
                      {status.details && (
                        <div className="text-xs text-gray-500 mt-1">
                          {status.details}
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleToggleActiveStatus(dumpster.id, dumpster.isActive !== false)}
                        className={`${
                          dumpster.isActive !== false 
                            ? 'text-red-600 hover:text-red-900' 
                            : 'text-green-600 hover:text-green-900'
                        }`}
                        title={dumpster.isActive !== false ? 'Désactiver' : 'Activer'}
                      >
                        {dumpster.isActive !== false ? (
                          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728L5.636 5.636m12.728 12.728L18.364 5.636" />
                          </svg>
                        ) : (
                          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                        )}
                      </button>
                      
                      <button
                        onClick={() => {
                          setSelectedDumpster(dumpster.id);
                          setEditDumpster({
                            name: dumpster.name,
                            number: dumpster.number,
                            comment: dumpster.comment || '',
                          });
                          setShowEditModal(true);
                        }}
                        className="text-blue-600 hover:text-blue-900"
                        title="Modifier"
                      >
                        <Edit className="h-5 w-5" />
                      </button>
                      
                      <button
                        onClick={() => {
                          setSelectedDumpster(dumpster.id);
                          setShowDeleteModal(true);
                        }}
                        className="text-red-600 hover:text-red-900"
                        title="Supprimer"
                      >
                        <Trash className="h-5 w-5" />
                      </button>
                      
                      {showMoveToDepotButton && (
                        <button
                          onClick={() => {
                            setSelectedDumpster(dumpster.id);
                            setShowMoveToDepotModal(true);
                          }}
                          className="text-green-600 hover:text-green-900"
                          title="Déplacer vers un dépôt"
                        >
                          <ArrowRight className="h-5 w-5" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      
      {/* Add Dumpster Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-bold mb-4">Ajouter une benne</h2>
            
            {error && (
              <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-lg">
                {error}
              </div>
            )}
            
            <form onSubmit={handleSubmit}>
              <div className="mb-4">
                <label htmlFor="name" className="block text-gray-700 font-medium mb-2">
                  Nom de la benne
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={newDumpster.name}
                  onChange={handleFormChange}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              
              <div className="mb-4">
                <label htmlFor="number" className="block text-gray-700 font-medium mb-2">
                  Numéro
                </label>
                <input
                  type="text"
                  id="number"
                  name="number"
                  value={newDumpster.number}
                  onChange={handleFormChange}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              
              <div className="mb-4">
                <label htmlFor="comment" className="block text-gray-700 font-medium mb-2">
                  Commentaire
                </label>
                <textarea
                  id="comment"
                  name="comment"
                  value={newDumpster.comment}
                  onChange={handleFormChange}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows={3}
                />
              </div>
              
              <div className="flex justify-end space-x-2">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddModal(false);
                    setError(null);
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-lg"
                  disabled={isSubmitting}
                >
                  Annuler
                </button>
                
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-800 text-white rounded-lg"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Création...' : 'Ajouter'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      
      {/* Edit Dumpster Modal */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-bold mb-4">Modifier la benne</h2>
            
            {error && (
              <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-lg">
                {error}
              </div>
            )}
            
            <form onSubmit={handleEditSubmit}>
              <div className="mb-4">
                <label htmlFor="editName" className="block text-gray-700 font-medium mb-2">
                  Nom de la benne
                </label>
                <input
                  type="text"
                  id="editName"
                  name="name"
                  value={editDumpster.name}
                  onChange={handleEditFormChange}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              
              <div className="mb-4">
                <label htmlFor="editNumber" className="block text-gray-700 font-medium mb-2">
                  Numéro
                </label>
                <input
                  type="text"
                  id="editNumber"
                  name="number"
                  value={editDumpster.number}
                  onChange={handleEditFormChange}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              
              <div className="mb-4">
                <label htmlFor="editComment" className="block text-gray-700 font-medium mb-2">
                  Commentaire
                </label>
                <textarea
                  id="editComment"
                  name="comment"
                  value={editDumpster.comment}
                  onChange={handleEditFormChange}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows={3}
                />
              </div>
              
              <div className="flex justify-end space-x-2">
                <button
                  type="button"
                  onClick={() => {
                    setShowEditModal(false);
                    setSelectedDumpster(null);
                    setError(null);
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-lg"
                  disabled={isSubmitting}
                >
                  Annuler
                </button>
                
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-800 text-white rounded-lg"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Enregistrement...' : 'Enregistrer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      
      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-bold mb-4">Supprimer la benne</h2>
            
            {error && (
              <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-lg">
                {error}
              </div>
            )}
            
            <p className="mb-4">
              Êtes-vous sûr de vouloir supprimer cette benne ? Cette action est irréversible.
            </p>
            
            <div className="flex justify-end space-x-2">
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setSelectedDumpster(null);
                  setError(null);
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg"
                disabled={isSubmitting}
              >
                Annuler
              </button>
              
              <button
                onClick={handleDelete}
                className="px-4 py-2 bg-red-600 text-white rounded-lg"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Suppression...' : 'Supprimer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Move to Depot Modal */}
      {showMoveToDepotModal && selectedDumpster && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-bold mb-4">Déplacer vers un dépôt</h2>
            
            <DumpsterMoveToDepotForm
              dumpsterId={selectedDumpster}
              onSuccess={() => {
                setShowMoveToDepotModal(false);
                setSelectedDumpster(null);
                loadInitialData();
              }}
              onCancel={() => {
                setShowMoveToDepotModal(false);
                setSelectedDumpster(null);
              }}
              loadInitialData={loadInitialData}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default DumpstersPage;